import { useEffect, useState, useMemo, memo } from "react";

import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Card } from "primereact/card";
import { Chart } from "primereact/chart";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import type { DataTablePageEvent } from "primereact/datatable";
import { MultiSelect } from "primereact/multiselect";
import { Skeleton } from "primereact/skeleton";
import { Tooltip } from "primereact/tooltip";

import { chartColors } from "sj-ui-toolkit/src/atlas/utils";
import { StatTile } from "sj-ui-toolkit/src/atlas/components/StatTile/StatTile";
import { getCweTags } from "sj-ui-toolkit/src/api/cweTags";
import { getIntegrationsClient } from "sj-ui-toolkit/src/client/svcClients";
import { type TimelineGranularity } from "sj-ui-toolkit/src/client/svcIntegrationsClient";
import { humanDate, humanDateWithoutTime } from "sj-ui-toolkit/src/utils/dates";
import { downloadCsv } from "sj-ui-toolkit/src/utils/downloadFile";
import { PERIODS } from "sj-ui-toolkit/src/constants/periods";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CweEntry {
  cwe?: string | number;
  username?: string;
  gitCommitterEmail?: string;
  gitRepo?: string;
  prNumber?: string | number;
  gitHeadSha?: string;
  createdAt?: string;
}

interface FilterOptions {
  emails: string[];
  cwes: string[];
}

interface CweReportData {
  totalCount?: number;
  topCwes?: { cwe?: number; count?: number }[];
  timeline?: { date?: string; count?: number }[];
  topContributors?: { email?: string; count?: number }[];
}

interface CweDescriptions {
  [key: string]: { Name?: string; Description?: string };
}

interface AspenAdaptCWEsProps {
  tenant: string;
  showRepository?: boolean;
  showUsername?: boolean;
  showPrNumber?: boolean;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const MemoizedTopCwesChart = memo(
  ({
    topCwes,
    cweDescriptions,
  }: {
    topCwes: { cwe?: number; count?: number }[];
    cweDescriptions: CweDescriptions | null;
  }) => {
    const chartData = useMemo(
      () => ({
        labels: topCwes.map(d => `CWE-${d.cwe ?? ""}`),
        datasets: [
          {
            data: topCwes.map(d => d.count ?? 0),
            backgroundColor: chartColors.getDojoChartColors()[0],
          },
        ],
      }),
      [topCwes]
    );

    const chartOptions = useMemo(
      () => ({
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: true,
            displayColors: false,
            callbacks: {
              title: (ctx: { label?: string }[]) => {
                const label = ctx[0]?.label ?? "";
                const cweNum = label.replace("CWE-", "");
                const name = cweDescriptions?.[cweNum]?.Name;
                return name ? `${label}: ${name}` : label;
              },
              label: (ctx: { parsed: { y: number } }) => `${ctx.parsed.y} occurrences`,
            },
          },
        },
        scales: {
          x: { display: false },
          y: { display: false, beginAtZero: true },
        },
      }),
      [cweDescriptions]
    );

    return <Chart type="bar" data={chartData} options={chartOptions} style={{ height: "120px" }} />;
  }
);

MemoizedTopCwesChart.displayName = "MemoizedTopCwesChart";

const MemoizedTimelineChart = memo(
  ({ timeline }: { timeline: { date?: string; count?: number }[] }) => {
    const chartData = useMemo(
      () => ({
        labels: timeline.map(d => d.date ?? ""),
        datasets: [
          {
            label: "CWEs",
            data: timeline.map(d => d.count ?? 0),
            fill: true,
            tension: 0.4,
            borderColor: chartColors.getDojoChartColors()[1],
            backgroundColor: chartColors.withChartFillOpacity(chartColors.getDojoChartColors()[1]),
            pointRadius: 3,
          },
        ],
      }),
      [timeline]
    );

    const chartOptions = useMemo(
      () => ({
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: true,
            displayColors: false,
            callbacks: {
              title: (ctx: { label?: string }[]) => humanDateWithoutTime(ctx[0]?.label ?? "") ?? "",
              label: (ctx: { parsed: { y: number } }) => `${ctx.parsed.y} CWEs`,
            },
          },
        },
        scales: {
          x: { display: false },
          y: { display: false, beginAtZero: true },
        },
      }),
      []
    );

    return (
      <Chart type="line" data={chartData} options={chartOptions} style={{ height: "120px" }} />
    );
  }
);

MemoizedTimelineChart.displayName = "MemoizedTimelineChart";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const AspenAdaptCWEs = ({
  tenant,
  showRepository = true,
  showUsername = true,
  showPrNumber = true,
}: AspenAdaptCWEsProps) => {
  const [report, setReport] = useState<CweReportData | null>(null);
  const [reportLoading, setReportLoading] = useState(true);
  const [period, setPeriod] = useState<{
    label: string;
    days: number;
    granularity: TimelineGranularity;
  }>(PERIODS[1]!);
  const [cweDescriptions, setCweDescriptions] = useState<CweDescriptions | null>(null);
  const [cwes, setCwes] = useState<CweEntry[]>([]);
  const [cweLoading, setCweLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<string | null>(null);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({ emails: [], cwes: [] });
  const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
  const [selectedCwes, setSelectedCwes] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<Date[] | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await getCweTags();
        setCweDescriptions(data);
      } catch {
        // ignore
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (!tenant) return;
    setReportLoading(true);
    const { promise, abort } = getIntegrationsClient().getCWEReport({
      tenant,
      daysBack: period.days,
      granularity: period.granularity,
    });
    const load = async () => {
      try {
        const data = await promise;
        setReport(data);
      } catch {
        setReport(null);
      } finally {
        setReportLoading(false);
      }
    };
    load();
    return abort;
  }, [tenant, period]);

  // Fetch CWEs whenever pagination/filter state changes
  useEffect(() => {
    if (dateRange !== null && (!dateRange[0] || !dateRange[1])) return;
    const fetchCWEs = async () => {
      setCweLoading(true);
      try {
        const integrationsClient = getIntegrationsClient();
        const getCWEsParams: Parameters<typeof integrationsClient.getCWEs>[0] = {
          tenant,
          page: page + 1,
          pageSize,
          ...(sortBy && { sortBy }),
          ...(sortOrder && { sortOrder }),
          ...(selectedEmails.length && { emails: selectedEmails }),
          ...(selectedCwes.length && { cwes: selectedCwes.map(Number) }),
          ...(dateRange?.[0] && { dateFrom: dateRange[0].toISOString() }),
          ...(dateRange?.[1] && { dateTo: dateRange[1].toISOString() }),
        };
        const { promise } = integrationsClient.getCWEs(getCWEsParams);
        const data = await promise;
        setCwes((data?.cwes ?? []) as CweEntry[]);
        setTotalRecords(data?.totalCount ?? 0);
        if (data?.filters) {
          setFilterOptions({
            emails: data.filters.emails ?? [],
            cwes: (data.filters.cwes ?? []).map(String),
          });
        }
      } catch (error) {
        console.error("Error fetching CWEs:", error);
        setCwes([]);
        setTotalRecords(0);
      } finally {
        setCweLoading(false);
      }
    };

    if (tenant) fetchCWEs();
  }, [tenant, page, pageSize, sortBy, sortOrder, selectedEmails, selectedCwes, dateRange]);

  const handleExportCWEs = async () => {
    try {
      setIsExporting(true);
      const integrationsClient = getIntegrationsClient();
      const { promise } = integrationsClient.exportCWEs({ tenant });

      // Endpoint writes the CSV data directly to the response body, so we expect a string here
      const data: unknown = await promise;

      if (typeof data !== "string" || data.trim() === "") {
        console.error("Failed to export CWEs: missing csvData in export response", data);
        return;
      }

      downloadCsv(data, `cwes-export-${new Date().toISOString().split("T")[0]}.csv`);
    } catch (error) {
      console.error("Failed to export CWEs:", error);
    } finally {
      setIsExporting(false);
    }
  };

  const handlePageChange = (event: DataTablePageEvent) => {
    if (event.rows !== pageSize) {
      setPageSize(event.rows);
      setPage(0);
    } else {
      setPage(event.page ?? 0);
    }
  };

  const handleSort = (event: { sortField?: string; sortOrder?: number | null }) => {
    if (event.sortField && event.sortOrder !== null && event.sortOrder !== undefined) {
      setSortBy(event.sortField);
      setSortOrder(event.sortOrder === 1 ? "asc" : "desc");
    } else {
      setSortBy(null);
      setSortOrder(null);
    }
    setPage(0);
  };

  const primeReactSortOrder: 1 | -1 | 0 | null | undefined =
    sortOrder === "asc" ? 1 : sortOrder === "desc" ? -1 : null;

  const dateTemplate = (rowData: CweEntry) => {
    if (!rowData.createdAt) return "--";
    return (
      <span title={humanDate(rowData.createdAt)}>{humanDateWithoutTime(rowData.createdAt)}</span>
    );
  };

  const emptyTemplate = (field: keyof CweEntry) => (rowData: CweEntry) => rowData[field] ?? "--";

  const cweTemplate = (rowData: CweEntry) => {
    if (!rowData.cwe) return "--";
    const entry = cweDescriptions?.[String(rowData.cwe)];
    let tooltip: string | undefined;
    if (entry?.Name) {
      tooltip = `CWE-${rowData.cwe}: ${entry.Name}`;
      if (entry.Description) tooltip += `\n\n${entry.Description}`;
    }
    return (
      <span className="cwe-col-tooltip" data-cwe={String(rowData.cwe)} data-pr-tooltip={tooltip}>
        {rowData.cwe}
      </span>
    );
  };

  const cweItemTemplate = (option: { value: string | number; label: string }) => {
    const entry = cweDescriptions?.[String(option.value)];
    const tooltip = entry?.Description ?? entry?.Name;
    return (
      <span className="cwe-opt-tooltip" data-pr-tooltip={tooltip}>
        {option.label}
      </span>
    );
  };

  const commitShaTemplate = (rowData: CweEntry) => {
    if (!rowData.gitHeadSha) return "--";
    return <span title={rowData.gitHeadSha}>{rowData.gitHeadSha.substring(0, 7)}</span>;
  };

  const topCwes = report?.topCwes ?? [];
  const timeline = report?.timeline ?? [];

  return (
    <>
      <Tooltip
        key={`cwe-col-${page}-${cwes.length}`}
        target=".cwe-col-tooltip"
        position="top"
        className="max-w-md whitespace-pre-line"
      />
      {cweDescriptions && <Tooltip key="cwe-opt" target=".cwe-opt-tooltip" position="right" />}

      <div className="flex items-center justify-between mt-6 mb-2">
        <h4 className="atlas-h4">Overview</h4>
        <div className="flex gap-2">
          {PERIODS.map(p => (
            <Button
              key={p.label}
              label={p.label}
              size="small"
              outlined={period.label !== p.label}
              onClick={() => setPeriod(p)}
            />
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <Card>
          {reportLoading ? (
            <Skeleton className="h-12 w-32" />
          ) : (
            <StatTile
              label="Total CWEs Recorded"
              value={report?.totalCount ?? "--"}
              barColor={chartColors.getDojoChartColors()[0]}
            />
          )}
        </Card>
        <Card>
          <p className="font-semibold mb-3">Training Opportunities</p>
          {reportLoading ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
            </div>
          ) : (report?.topContributors?.length ?? 0) === 0 ? (
            <p className="body-sm text-secondary">No data</p>
          ) : (
            <div className="flex flex-col gap-2">
              {report!.topContributors!.slice(0, 3).map((c, i) => (
                <div key={c.email ?? i} className="flex items-center justify-between body-sm">
                  <span className="truncate mr-2">{c.email ?? "--"}</span>
                  <span className="font-semibold shrink-0">{c.count ?? 0} CWEs</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <Card>
          <p className="font-semibold mb-3">Top CWEs by Frequency</p>
          {reportLoading ? (
            <Skeleton className="w-full h-32" />
          ) : topCwes.length === 0 ? (
            <p className="body-sm text-secondary">No data</p>
          ) : (
            <div style={{ height: "120px" }}>
              <MemoizedTopCwesChart topCwes={topCwes} cweDescriptions={cweDescriptions} />
            </div>
          )}
        </Card>
        <Card>
          <p className="font-semibold mb-3">CWEs Over Time</p>
          {reportLoading ? (
            <Skeleton className="w-full h-32" />
          ) : timeline.length === 0 ? (
            <p className="body-sm text-secondary">No data</p>
          ) : (
            <div style={{ height: "120px" }}>
              <MemoizedTimelineChart timeline={timeline} />
            </div>
          )}
        </Card>
      </div>

      <h4 className="atlas-h4 mt-6 mb-4">Activity Log</h4>
      <div className="flex items-center justify-between mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <MultiSelect
            value={selectedEmails}
            options={filterOptions.emails}
            onChange={e => setSelectedEmails((e.value as string[] | null) ?? [])}
            placeholder="Filter by email"
            maxSelectedLabels={1}
            selectedItemsLabel="{0} emails selected"
            filter
            showClear
            className="w-56"
          />
          <MultiSelect
            value={selectedCwes}
            options={(filterOptions.cwes ?? []).map(c => {
              const name = cweDescriptions?.[String(c)]?.Name;
              return { label: name ? `CWE-${c} (${name})` : `CWE-${c}`, value: c };
            })}
            itemTemplate={cweItemTemplate}
            onChange={e => setSelectedCwes((e.value as string[] | null) ?? [])}
            placeholder="Filter by CWE"
            maxSelectedLabels={1}
            selectedItemsLabel="{0} CWEs selected"
            filter
            showClear
            className="w-56"
          />
          <Calendar
            value={dateRange as Date[] | undefined}
            onChange={e => setDateRange(e.value as Date[] | null)}
            selectionMode="range"
            placeholder="Select date range"
            showButtonBar
            hideOnRangeSelection
            dateFormat="mm/dd/yy"
            maxDate={new Date()}
            className="w-56"
          />
          <Button
            label="Clear Filters"
            icon="pi pi-filter-slash"
            severity="secondary"
            onClick={() => {
              setSelectedEmails([]);
              setSelectedCwes([]);
              setDateRange(null);
            }}
          />
        </div>
        <Button
          label="Export data"
          icon="pi pi-download"
          iconPos="left"
          disabled={isExporting}
          onClick={handleExportCWEs}
          tooltip="Export all data as a CSV"
          tooltipOptions={{ position: "top" }}
          className="shrink-0 ml-2"
        />
      </div>

      <Card>
        <DataTable
          key={`cwe-table-${!!cweDescriptions}`}
          value={cweLoading ? Array(pageSize).fill(null) : cwes}
          className="p-datatable-sm"
          tableClassName="dt-responsive-text"
          aria-label="CWE activity log"
          sortMode="single"
          sortField={sortBy ?? undefined}
          sortOrder={primeReactSortOrder}
          onSort={handleSort}
          paginator
          lazy
          rows={pageSize}
          totalRecords={totalRecords}
          first={page * pageSize}
          onPage={handlePageChange}
          rowsPerPageOptions={[10, 25, 50, 100]}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
          emptyMessage="No Data"
        >
          <Column
            field="cwe"
            header="CWE"
            sortable
            body={(row: CweEntry) => (cweLoading ? <Skeleton height="16px" /> : cweTemplate(row))}
          />
          {showUsername && (
            <Column
              field="username"
              header="Username"
              sortable
              body={(row: CweEntry) =>
                cweLoading ? <Skeleton height="16px" /> : emptyTemplate("username")(row)
              }
            />
          )}
          <Column
            field="gitCommitterEmail"
            header="Email"
            sortable
            body={(row: CweEntry) =>
              cweLoading ? <Skeleton height="16px" /> : emptyTemplate("gitCommitterEmail")(row)
            }
          />
          {showRepository && (
            <Column
              field="gitRepo"
              header="Repository"
              sortable
              body={(row: CweEntry) =>
                cweLoading ? <Skeleton height="16px" /> : emptyTemplate("gitRepo")(row)
              }
            />
          )}
          {showPrNumber && (
            <Column
              field="prNumber"
              header="PR Number"
              body={(row: CweEntry) =>
                cweLoading ? <Skeleton height="16px" /> : emptyTemplate("prNumber")(row)
              }
            />
          )}
          <Column
            field="gitHeadSha"
            header="Commit SHA"
            body={(row: CweEntry) =>
              cweLoading ? <Skeleton height="16px" /> : commitShaTemplate(row)
            }
          />
          <Column
            field="createdAt"
            header="Created At"
            sortable
            body={(row: CweEntry) => (cweLoading ? <Skeleton height="16px" /> : dateTemplate(row))}
          />
        </DataTable>
      </Card>
    </>
  );
};

export default AspenAdaptCWEs;
